import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { MenuPopup } from '@prosolve/common-components-ui';
import { FiLink, FiEdit2, FiTrash2, FiDownload, FiEye, FiMoreVertical } from 'react-icons/fi';
import ChipButton from './ChipButton';
import { downloadAssetFile } from './downloadUtils';
import { assetTheme as t } from './assetTheme';
import { isImageFile, getFileIcon, getFileExtensionLabel, formatBytes } from './fileTypeUtils';

const Card = styled.div`
  position: relative;
  background: ${({ $isSelected }) => $isSelected ? t.teal50 : t.surface};
  border: 1px solid ${({ $isSelected }) => $isSelected ? t.tealDark : t.border};
  box-shadow: ${({ $isSelected }) => $isSelected ? `0 0 0 1px ${t.tealDark}` : 'none'};
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  opacity: ${({ $isDragging }) => $isDragging ? 0.4 : 1};
  transition: border-color .12s, box-shadow .12s, transform .12s;

  &:hover {
    border-color: ${({ $isSelected }) => $isSelected ? t.tealDark : t.tealBrand};
    box-shadow: ${({ $isSelected }) => $isSelected ? `0 0 0 1px ${t.tealDark}, 0 8px 22px rgba(0,19,35,.12)` : '0 8px 22px rgba(0,19,35,.12)'};
    transform: translateY(-2px);
  }
`

const Thumb = styled.div`
  height: 150px;
  background: ${t.card2};
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid ${t.border};
  font-size: 40px;
  color: ${t.faint};
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const Meta = styled.div`
  padding: 12px 14px;
`

const Name = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${t.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Sub = styled.div`
  font-size: 12px;
  color: ${t.faint};
  margin-top: 3px;
`

// Hidden via visibility (not display) so the chips keep their layout and the
// fit-detection below can measure them while the card isn't hovered
const HoverBar = styled.div`
  position: absolute;
  top: 10px;
  right: 5px;
  display: flex;
  visibility: hidden;
  align-items: center;
  gap: 6px;
  z-index: 1;

  ${Card}:hover & {
    visibility: visible;
  }
`

const MENU_WIDTH = 140;

export default ({
  file,
  isSelectMode,
  isSelected,
  onSelect,
  onPreview,
  onRename,
  onDelete
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [isCondensed, setIsCondensed] = useState(false)
  const [menuPos, setMenuPos] = useState(null)
  const timeoutRef = useRef()
  const cardRef = useRef()
  const barRef = useRef()
  const fullBarWidthRef = useRef(0)
  const isCondensedRef = useRef(false)

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current)
  }, [])

  // Collapse the download/rename/delete chips behind a 3-dots menu only when the
  // full chip set is wider than the card (e.g. select mode adds a preview chip)
  useEffect(() => {
    const evaluateFit = () => {
      if (!cardRef.current || !barRef.current) return;
      // Only remeasure while the full set is rendered, so the stored width can
      // un-collapse the bar again if the card grows
      if (!isCondensedRef.current) {
        fullBarWidthRef.current = barRef.current.scrollWidth;
      }
      // 5px right offset + matching breathing room on the left edge
      const available = cardRef.current.clientWidth - 10;
      const shouldCondense = fullBarWidthRef.current > available;
      isCondensedRef.current = shouldCondense;
      setIsCondensed(shouldCondense);
    }
    evaluateFit();
    const observer = new ResizeObserver(evaluateFit);
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [])

  const onOpenMenu = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + window.scrollY + 6,
      left: rect.right + window.scrollX - MENU_WIDTH
    });
  }

  const onCopyUrl = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(file.url);
    setIsCopied(true);
    timeoutRef.current = setTimeout(() => setIsCopied(false), 2000);
  }

  return (
    <Card
      ref={cardRef}
      $isDragging={isDragging}
      $isSelected={isSelected}
      draggable={true}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', `assetfile:${file.key}`);
        e.dataTransfer.effectAllowed = 'move';
        setIsDragging(true);
      }}
      onDragEnd={() => setIsDragging(false)}
      onClick={() => isSelectMode ? onSelect(file) : onPreview(file)}
    >
      <HoverBar ref={barRef}>
        {
          isSelectMode &&
          <ChipButton $variant='light' title='Preview file' onClick={(e) => { e.stopPropagation(); onPreview(file); }}>
            <FiEye />
          </ChipButton>
        }
        <ChipButton $variant='copy' onClick={onCopyUrl}>
          <FiLink />
          {isCopied ? 'Copied!' : 'Copy URL'}
        </ChipButton>
        {
          isCondensed ?
          <ChipButton $variant='light' title='More actions' onClick={onOpenMenu}>
            <FiMoreVertical />
          </ChipButton>
          :
          <>
            <ChipButton $variant='light' title='Download file' onClick={(e) => { e.stopPropagation(); downloadAssetFile(file); }}>
              <FiDownload />
            </ChipButton>
            <ChipButton $variant='light' title='Rename file' onClick={(e) => { e.stopPropagation(); onRename(file); }}>
              <FiEdit2 />
            </ChipButton>
            <ChipButton $variant='light' title='Delete file' onClick={(e) => { e.stopPropagation(); onDelete(file); }}>
              <FiTrash2 />
            </ChipButton>
          </>
        }
      </HoverBar>
      {createPortal(
        <MenuPopup
          open={!!menuPos}
          top={menuPos?.top}
          left={menuPos?.left}
          width={MENU_WIDTH}
          onClose={() => setMenuPos(null)}
          options={['Download', 'Rename', 'Delete']}
          onSelection={(val) => {
            if (val === 'Download') downloadAssetFile(file);
            if (val === 'Rename') onRename(file);
            if (val === 'Delete') onDelete(file);
          }}
        />,
        document.body
      )}
      <Thumb>
        {
          isImageFile(file.name) ?
          <img src={file.url} alt={file.name} loading='lazy' draggable={false} />
          :
          getFileIcon(file.name)
        }
      </Thumb>
      <Meta>
        <Name>{file.name}</Name>
        <Sub>{getFileExtensionLabel(file.name)}{file.size || file.size === 0 ? ` · ${formatBytes(file.size)}` : ''}</Sub>
      </Meta>
    </Card>
  )
}
